import { ManifestType } from '@clientos/types';
import logger from '../../interfaces/logger';

// ============================================================================
// Constants
// ============================================================================

const FAVICON_ICON_PATHS = {
  highContrast: '/assets/icons/icon-high-contrast-56x56.png',
  darkMode: '/assets/icons/icon-dark-mode-56x56.png',
  default: '/assets/icons/icon-48x48.png'
} as const;

// ============================================================================
// Helpers
// ============================================================================

function buildFaviconPaths(manifest: ManifestType | undefined): {
  highContrast: string;
  darkMode: string;
  default: string;
} {
  const useFullDomain = manifest?.portal?.useFullDomainOnAssets;

  if (!useFullDomain) {
    return FAVICON_ICON_PATHS;
  }
  const defaultFullDomain = window.defaultUrl ?? '';

  const paths = {
    highContrast: `${defaultFullDomain}${FAVICON_ICON_PATHS.highContrast}`,
    darkMode: `${defaultFullDomain}${FAVICON_ICON_PATHS.darkMode}`,
    default: `${defaultFullDomain}${FAVICON_ICON_PATHS.default}`
  };

  console.info(
    '[FaviconLoader] Using default full domain for favicon assets:',
    paths
  );

  return paths;
}

// ============================================================================
// FaviconLoader Service
// ============================================================================

/**
 * Service for initializing the favicon based on user display preferences.
 *
 * Detects high-contrast mode and dark mode via media queries and injects
 * the appropriate favicon link element into the document head.
 *
 * These assets should be provided by the hosting portal in their CDN,
 * even though we load them from the composer
 *
 * When `manifest.portal.useFullDomainOnAssets` is `true`, icon paths are
 * prefixed with `window.defaultUrl` instead of using relative paths.
 */
class FaviconLoader {
  /**
   * Initializes the favicon based on the user's display preferences
   * (high contrast, dark mode, or default).
   *
   * @param manifest - Optional manifest used to resolve the asset base domain.
   */
  init(manifest?: ManifestType): void {
    try {
      const isHighContrast = window.matchMedia('(forced-colors: active)');
      const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)');

      const buildedfaviconPaths = buildFaviconPaths(manifest);

      const faviconNode = document.createElement('link');
      faviconNode.rel = 'icon';

      if (isHighContrast?.matches) {
        faviconNode.href = buildedfaviconPaths.highContrast;
      } else if (isDarkMode?.matches) {
        faviconNode.href = buildedfaviconPaths.darkMode;
      } else {
        faviconNode.href = buildedfaviconPaths.default;
      }

      document.head.appendChild(faviconNode);
    } catch (error) {
      logger.debug('Failed to initialize favicon:', error);
    }
  }
}

const faviconLoader = new FaviconLoader();

export default faviconLoader;
